import { FC } from "react";

import { Box } from "@chakra-ui/react";
import { useTranslation } from "next-i18next";

import { FormMessage, FullWidthVStack } from "@components/common";
import Icon from "@components/Icon";
import Link from "@components/Link";
import Loader from "@components/Loader";
import { OptionButtonGroup } from "@components/OptionButton";
import { Desktop, Mobile } from "@lib/media";
import responsiveStyle from "@lib/theme/responsiveStyle";
import { withCompositeFormikField, TCompositeFormikFieldProps } from "@lib/withFormikField";
import { PACKAGE_SIZE_OPTION_DESKTOP, PACKAGE_SIZE_OPTION_MOBILE } from "@utils/constants/qa";

import { OrderFields } from "../../constants";
import useOrderData from "../../context/useOrderData";
import useOrderFieldsLoading from "../../context/useOrderFieldsLoading";
import useOrderFormik from "../../hooks/useOrderFormik";
import SectionTitle from "../common/SectionTitle";
import PackageSizeCard from "./PackageSizeCard";

type TPackageSizeProps = {
  name: OrderFields;
  value?: string;
} & TCompositeFormikFieldProps;

const DESKTOP_CARD_WIDTHS = ["9.5rem", "9.5rem", "9.5rem", "10.75rem", "12rem"];

const PackageSize: FC<TPackageSizeProps> = ({ name, value, setFieldValue }) => {
  const { t } = useTranslation();

  const {
    errors: { [name]: fieldError },
  } = useOrderFormik();

  const {
    dynamicData: { packageSizes },
  } = useOrderData();

  const { isFieldLoading } = useOrderFieldsLoading();
  const isLoading = isFieldLoading(OrderFields.PACKAGE_SIZE);

  const sizeOptions = packageSizes.map((item, index) => {
    const desktopCardWidth =
      index < DESKTOP_CARD_WIDTHS.length
        ? DESKTOP_CARD_WIDTHS[index]
        : DESKTOP_CARD_WIDTHS[DESKTOP_CARD_WIDTHS.length - 1];

    return {
      label: (
        <PackageSizeCard
          title={item.title}
          description={item.description || ""}
          image={item.image}
          activeImage={item.hoverImage || item.image}
          weight={item.weight || ""}
          isActive={value === item.title}
        />
      ),
      value: item.title,
      width: responsiveStyle({ mobile: "auto", desktop: desktopCardWidth }),
    };
  });

  const handleOptionSelect = (val: any) => {
    setFieldValue?.(val);
  };

  return (
    <Loader isLoading={!!isLoading}>
      <FullWidthVStack>
        <SectionTitle>{t("order-package-package_size")}</SectionTitle>
        <Box
          width={responsiveStyle({
            mobile: sizeOptions.length === 1 ? "calc(33.333333% - 0.3125rem)" : "100%",
            desktop: "100%",
          })}
        >
          <Mobile>
            <OptionButtonGroup
              isFormik={false}
              testId={PACKAGE_SIZE_OPTION_MOBILE}
              name={`mobile-options-${name}`}
              options={sizeOptions}
              height="9.625rem"
              value={value}
              onChange={handleOptionSelect}
              lines={[
                { length: 3, height: "6.875rem" },
                { length: 2, height: "7.8rem", width: "50%" },
              ]}
            />
          </Mobile>
          <Desktop>
            <OptionButtonGroup
              isFormik={false}
              testId={PACKAGE_SIZE_OPTION_DESKTOP}
              name={`desktop-options-${name}`}
              options={sizeOptions}
              height="9.625rem"
              value={value}
              onChange={handleOptionSelect}
            />
          </Desktop>
        </Box>
        {fieldError && <FormMessage message={fieldError} isInvalid />}
        <Box pt="0.75rem">
          <Link
            label={t("order-package-packing_guide")}
            href={t("order-package-packing_guide-href")}
            leftIcon={<Icon icon="external" />}
            isExternal
            isUnderlined
            fontWeight={300}
          />
        </Box>
      </FullWidthVStack>
    </Loader>
  );
};
export default withCompositeFormikField({ hasSetFieldValue: true })(PackageSize);
